import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  Checkbox,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { userProjectService } from '../../services/userProjectService';

/**
 * Subordinate Project Assignment Dialog
 * For SuperUsers to assign their own projects to subordinates
 */
const SubordinateProjectAssignmentDialog = ({ open, onClose, subordinate, myProjects, onSuccess, onError }) => {
  const [subordinateProjects, setSubordinateProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && subordinate) {
      loadSubordinateProjects();
    }
  }, [open, subordinate]);

  const loadSubordinateProjects = async () => {
    setLoading(true);
    try {
      // Load subordinate's current projects
      const assignments = await userProjectService.getUserProjects(subordinate.user_id);
      setSubordinateProjects(assignments);

      // Pre-select projects that are both:
      // 1. In my projects list
      // 2. Already assigned to subordinate
      const myProjectCodes = myProjects.map((p) => p.project_code);
      const subordinateProjectCodes = assignments.map((a) => a.project_code);
      const intersection = myProjectCodes.filter((code) => subordinateProjectCodes.includes(code));

      setSelectedProjects(intersection);
    } catch (err) {
      onError('Failed to load subordinate projects: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProject = (projectCode) => {
    setSelectedProjects((prev) => {
      if (prev.includes(projectCode)) {
        return prev.filter((code) => code !== projectCode);
      } else {
        return [...prev, projectCode];
      }
    });
  };

  const handleSelectAll = () => {
    const filteredCodes = getFilteredProjects().map((p) => p.project_code);
    setSelectedProjects(filteredCodes);
  };

  const handleDeselectAll = () => {
    setSelectedProjects([]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // For each selected project, assign to subordinate
      const promises = [];

      // Get current assignments that are from "my projects"
      const myProjectCodes = myProjects.map((p) => p.project_code);
      const currentAssignments = subordinateProjects
        .filter((a) => myProjectCodes.includes(a.project_code))
        .map((a) => a.project_code);

      // Projects to add (in selected but not in current)
      const toAdd = selectedProjects.filter((code) => !currentAssignments.includes(code));

      // Projects to remove (in current but not in selected)
      const toRemove = currentAssignments.filter((code) => !selectedProjects.includes(code));

      // Add new assignments
      for (const projectCode of toAdd) {
        promises.push(
          userProjectService.assignToSubordinate(subordinate.user_id, projectCode)
        );
      }

      // Remove assignments
      for (const projectCode of toRemove) {
        promises.push(
          userProjectService.unassignFromSubordinate(subordinate.user_id, projectCode)
        );
      }

      await Promise.all(promises);

      const message = `Successfully updated projects: ${toAdd.length} assigned, ${toRemove.length} removed`;
      onSuccess(message);
      onClose(true); // Close with reload
    } catch (err) {
      onError('Failed to update projects: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const getFilteredProjects = () => {
    if (!searchTerm) return myProjects;

    return myProjects.filter(
      (proj) =>
        proj.project_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (proj.project?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredProjects = getFilteredProjects();

  // Get initial state
  const myProjectCodes = myProjects.map((p) => p.project_code);
  const initiallyAssigned = subordinateProjects
    .filter((a) => myProjectCodes.includes(a.project_code))
    .map((a) => a.project_code);

  const hasChanges =
    JSON.stringify([...selectedProjects].sort()) !== JSON.stringify([...initiallyAssigned].sort());

  // Projects assigned by others (not managed by this SuperUser)
  const otherProjects = subordinateProjects.filter(
    (a) => !myProjectCodes.includes(a.project_code)
  );

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, height: '80vh' },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Assign Projects to: {subordinate?.username}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {subordinate?.email} • Assign from your {myProjects.length} project(s)
            </Typography>
          </Box>
          <IconButton onClick={() => onClose(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Info Alert */}
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
              You can only assign projects that are assigned to you. To give more access, contact
              your administrator.
            </Alert>

            {/* Summary */}
            <Box sx={{ mb: 2 }}>
              <Alert severity={hasChanges ? 'warning' : 'success'} icon={<CheckCircleIcon />}>
                <strong>{selectedProjects.length}</strong> of <strong>{myProjects.length}</strong>{' '}
                your projects will be assigned
                {hasChanges && ' (unsaved changes)'}
              </Alert>
            </Box>

            {/* Other Projects Info */}
            {otherProjects.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This user also has <strong>{otherProjects.length}</strong> other project(s) assigned
                by the administrator (not shown here)
              </Alert>
            )}

            {/* Search */}
            <TextField
              fullWidth
              size="small"
              placeholder="Search your projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ mb: 2 }}
            />

            {/* Select/Deselect All */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button size="small" variant="outlined" onClick={handleSelectAll}>
                Select All
              </Button>
              <Button size="small" variant="outlined" onClick={handleDeselectAll}>
                Deselect All
              </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Project List */}
            {filteredProjects.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {myProjects.length === 0
                    ? 'You have no projects to assign'
                    : 'No projects found matching your search'}
                </Typography>
              </Box>
            ) : (
              <List sx={{ maxHeight: '350px', overflow: 'auto' }}>
                {filteredProjects.map((proj) => {
                  const isSelected = selectedProjects.includes(proj.project_code);
                  const wasInitiallyAssigned = initiallyAssigned.includes(proj.project_code);

                  return (
                    <ListItem
                      key={proj.project_code}
                      button
                      onClick={() => handleToggleProject(proj.project_code)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={isSelected}
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {proj.project_code}
                            </Typography>
                            {!wasInitiallyAssigned && isSelected && (
                              <Chip label="NEW" size="small" color="success" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant="body2">
                            {proj.project?.name || 'Project'}
                          </Typography>
                        }
                      />
                      <Chip
                        label={proj.project?.status || 'Unknown'}
                        size="small"
                        color={proj.project?.status === 'Active' ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={() => onClose(false)} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !hasChanges || loading || myProjects.length === 0}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            px: 3,
          }}
        >
          {saving ? 'Assigning...' : 'Assign Projects'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubordinateProjectAssignmentDialog;
